import { useState } from "react";
import { COMPANY } from "../../data/site";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    topic: "Consulting enquiry",
    message: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    const subject = encodeURIComponent(
      `[${form.topic}] Enquiry from ${form.name}`
    );
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\nTopic: ${form.topic}\n\n${form.message}`
    );
    toast.success("Opening your email client…", {
      description: "If nothing happens, write to " + COMPANY.email,
    });
    setTimeout(() => {
      window.location.href = `mailto:${COMPANY.email}?subject=${subject}&body=${body}`;
    }, 600);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#FFFBF2] text-[#0A0A0A] py-24 md:py-36 border-t border-black/10 relative overflow-hidden"
    >
      {/* warm wash */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(50% 60% at 0% 0%, rgba(47,107,31,0.10), transparent 60%), radial-gradient(40% 50% at 100% 100%, rgba(245,165,36,0.12), transparent 60%)",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-6 h-px bg-[#2F6B1F]" />
              <span className="font-mono-ibm text-[11px] tracking-[0.2em] uppercase text-[#2F6B1F]">
                Contact / 06
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] font-medium">
              Start a project.
              <br />
              Get a <span className="text-[#2F6B1F]">senior</span> reply.
            </h2>
            <p className="mt-8 text-base md:text-lg text-[#404040] max-w-md leading-relaxed">
              Briefs go directly to the partner team. Expect a substantive
              response within one business day.
            </p>

            <div className="mt-12 space-y-6 font-mono-ibm text-sm">
              <div className="border-t border-black/15 pt-5">
                <div className="text-[11px] uppercase tracking-widest text-[#404040]">
                  Office
                </div>
                <div className="mt-2 text-[#0A0A0A] font-display text-lg leading-snug">
                  {COMPANY.address.line1}
                  <br />
                  {COMPANY.address.line2} {COMPANY.address.postal}
                  <br />
                  {COMPANY.address.country}
                </div>
              </div>
              <div className="border-t border-black/15 pt-5">
                <div className="text-[11px] uppercase tracking-widest text-[#404040]">
                  Direct line
                </div>
                <a
                  href={`tel:${COMPANY.phone.replace(/[^+\d]/g, "")}`}
                  data-testid="contact-phone-link"
                  className="mt-2 inline-block font-display text-lg text-[#0A0A0A] hover:text-[#2F6B1F] transition-colors"
                >
                  {COMPANY.phone}
                </a>
                <div className="mt-1 text-[#404040] text-xs">{COMPANY.hours}</div>
              </div>
              <div className="border-t border-black/15 pt-5">
                <div className="text-[11px] uppercase tracking-widest text-[#404040]">
                  Email
                </div>
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="contact-email-link"
                  className="mt-2 inline-block font-display text-lg text-[#0A0A0A] hover:text-[#2F6B1F] transition-colors"
                >
                  {COMPANY.email}
                </a>
              </div>
            </div>
          </div>

          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="lg:col-span-7 border border-black/15 p-8 md:p-12 bg-white shadow-sm"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Full name" id="name" value={form.name} onChange={update("name")} testid="contact-input-name" />
              <Field label="Email" id="email" type="email" value={form.email} onChange={update("email")} testid="contact-input-email" />
              <Field label="Company" id="company" value={form.company} onChange={update("company")} testid="contact-input-company" />
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="topic"
                  className="text-[11px] uppercase tracking-widest text-[#404040] font-mono-ibm"
                >
                  Topic
                </label>
                <select
                  id="topic"
                  data-testid="contact-input-topic"
                  value={form.topic}
                  onChange={update("topic")}
                  className="bg-transparent border-b border-black/30 py-3 text-[#0A0A0A] focus:border-[#2F6B1F] outline-none font-display text-lg"
                >
                  <option>Consulting enquiry</option>
                  <option>New project / RFP</option>
                  <option>Staff augmentation</option>
                  <option>Partnership</option>
                  <option>Press / Media</option>
                </select>
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-2">
              <label
                htmlFor="message"
                className="text-[11px] uppercase tracking-widest text-[#404040] font-mono-ibm"
              >
                Brief
              </label>
              <textarea
                id="message"
                data-testid="contact-input-message"
                rows={6}
                value={form.message}
                onChange={update("message")}
                placeholder="A few lines on what you’re trying to solve…"
                className="bg-transparent border-b border-black/30 py-3 text-[#0A0A0A] focus:border-[#2F6B1F] outline-none resize-none font-display text-lg placeholder:text-black/30"
              />
            </div>

            <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <p className="text-xs text-[#404040] font-mono-ibm max-w-md">
                We treat all enquiries as strictly confidential. By submitting,
                you agree to be contacted about your enquiry only.
              </p>
              <button
                type="submit"
                data-testid="contact-submit-button"
                className="bg-[#2F6B1F] text-white px-8 py-4 font-medium hover:bg-[#FF331F] transition-colors duration-300"
              >
                Send enquiry →
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, id, value, onChange, type = "text", testid }) {
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="text-[11px] uppercase tracking-widest text-[#404040] font-mono-ibm"
      >
        {label}
      </label>
      <input
        id={id}
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        className="bg-transparent border-b border-black/30 py-3 text-[#0A0A0A] focus:border-[#2F6B1F] outline-none font-display text-lg"
      />
    </div>
  );
}
